import React, { useEffect, useState } from 'react';
import { Phone, Mail, Building } from 'lucide-react';
import axiosInstance from '../api';
import { useLang } from '../i18n/LanguageContext';

// `code` matches tank_mst.owned_by — water-body counts come live from the
// public portal-stats endpoint (same source as the Stakeholder cards).
// HOD / designated-officer details as confirmed by each department (Oct 2026).
// `hod` is the HOD designation [english, tamil]; `phones` / `emails` are lists.
// Departments still to confirm have hod: null and show "To be updated".
const departments = [
  { code: 'WRD',    department: ['Water Resources Department', 'நீர்வளத் துறை'],
    hod: ['Engineer-in-Chief & Chief Engineer (General), WRD', 'முதன்மைத் தலைமைப் பொறியாளர் மற்றும் தலைமைப் பொறியாளர் (பொது), நீர்வளத் துறை'],
    phones: ['044-28525351'], emails: ['eicwrdtn@gmail.com'] },
  { code: 'RDPR',   department: ['Rural Development and Panchayat Raj', 'ஊரக வளர்ச்சி மற்றும் ஊராட்சித் துறை'],
    hod: ['Commissioner, RD&PR', 'ஆணையர், ஊரக வளர்ச்சி மற்றும் ஊராட்சித் துறை'],
    phones: ['044-24323794'], emails: ['drdchamber@gmail.com'] },
  { code: 'DTP',    department: ['Town Panchayat', 'பேரூராட்சிகள் இயக்குநரகம்'],
    hod: ['Director of Town Panchayats', 'பேரூராட்சிகள் இயக்குநர்'],
    phones: ['044-29520050'], emails: ['dtpsystem@gmail.com', 'dtp.tn@nic.in'] },
  { code: 'DMA',    department: ['Directorate of Municipal Administration', 'நகராட்சி நிர்வாக இயக்குநரகம்'],
    hod: ['Director of Municipal Administration', 'நகராட்சி நிர்வாக இயக்குநர்'],
    phones: ['044-29864457'], emails: ['cma.tncma@nic.in'] },
  { code: 'GCC',    department: ['Greater Chennai Corporation', 'பெருநகர சென்னை மாநகராட்சி'],
    hod: ['Superintending Engineer, Stormwater Drain Department', 'கண்காணிப்புப் பொறியாளர், மழைநீர் வடிகால் துறை'],
    phones: ['044-25619315', '9445190732'], emails: ['seswdd2gcc@gmail.com'] },
  { code: 'FOREST', department: ['Environment, Climate Change & Forest', 'சுற்றுச்சூழல், காலநிலை மாற்றம் மற்றும் வனத் துறை'],
    hod: ['Principal Chief Conservator of Forests (Head of Forest Force)', 'முதன்மைத் தலைமை வனப் பாதுகாவலர் (வனப் படைத் தலைவர்)'],
    phones: ['044-24348059'], emails: ['tnforest@nic.in'] },
  { code: 'HRCE',   department: ['Hindu Religious & Charitable Endowment', 'இந்து சமய அறநிலையத் துறை'],
    hod: ['Commissioner, HR&CE Department', 'ஆணையர், இந்து சமய அறநிலையத் துறை'],
    phones: ['044-28339999', '044-28334815'], emails: ['commr.hrce@tn.gov.in'] },
];

const ContactPage = () => {
  const { tr } = useLang();
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axiosInstance
      .get('/applications/public/portal-stats', { silent: true })
      .then((res) => {
        const rows = res?.data?.data?.tanks?.by_department || [];
        setCounts(Object.fromEntries(rows.map((r) => [r.department_code, r.tank_count])));
      })
      .catch(() => setCounts({}))
      .finally(() => setLoading(false));
  }, []);

  const total = departments.reduce((sum, d) => sum + Number(counts[d.code] || 0), 0);
  const fmt = (n) => Number(n || 0).toLocaleString('en-IN');

  return (
    <div className="min-h-screen bg-slate-100 pt-20">
      {/* Page header */}
      <div className="relative bg-cover bg-center border-b-4 border-brand-accent" style={{ backgroundImage: "url('/images/water3.jpg')" }}>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-700/95 via-brand-600/85 to-teal-500/70" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-8 py-10">
          <h1 className="text-3xl lg:text-4xl font-light text-white">
            {tr('Department Directory', 'துறை தொடர்பு விவரங்கள்')}
          </h1>
          <p className="mt-2 text-white/90 max-w-2xl">
            {tr('Head of Department / designated officer contacts for each stakeholder department', 'ஒவ்வொரு பங்குதாரர் துறையின் துறைத் தலைவர் / நியமிக்கப்பட்ட அலுவலர் தொடர்பு விவரங்கள்')}
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-10">
        <div className="bg-white border border-slate-200 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-brand-700 text-white text-left text-xs uppercase tracking-wide">
                <th className="px-4 py-3 font-semibold text-center w-14">{tr('S.No', 'வ.எண்')}</th>
                <th className="px-4 py-3 font-semibold">{tr('Department', 'துறை')}</th>
                <th className="px-4 py-3 font-semibold">{tr('HOD / Designated Officer', 'துறைத் தலைவர் / நியமிக்கப்பட்ட அலுவலர்')}</th>
                <th className="px-4 py-3 font-semibold">{tr('Contact Number', 'தொடர்பு எண்')}</th>
                <th className="px-4 py-3 font-semibold">{tr('Email ID', 'மின்னஞ்சல்')}</th>
                <th className="px-4 py-3 font-semibold text-right">{tr('Water Bodies', 'நீர்நிலைகள்')}</th>
              </tr>
            </thead>
            <tbody>
              {departments.map((dept, i) => {
                const pending = <span className="text-slate-400 italic">{tr('To be updated', 'புதுப்பிக்கப்படும்')}</span>;
                return (
                  <tr key={dept.code} className="odd:bg-white even:bg-brand-50 border-b border-slate-100 align-top">
                    <td className="px-4 py-3 text-center text-slate-500">{i + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2">
                        <Building size={16} className="text-brand-600 mt-0.5 flex-shrink-0" />
                        <span className="font-medium text-slate-800">{tr(...dept.department)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{dept.hod ? tr(...dept.hod) : pending}</td>
                    <td className="px-4 py-3">
                      {dept.phones.length ? dept.phones.map((ph) => (
                        <a key={ph} href={`tel:${ph.replace(/[^0-9+]/g, '')}`} className="flex items-center gap-1.5 text-slate-700 hover:text-brand-700 whitespace-nowrap">
                          <Phone size={14} className="text-slate-500" /> {ph}
                        </a>
                      )) : pending}
                    </td>
                    <td className="px-4 py-3">
                      {dept.emails.length ? dept.emails.map((em) => (
                        <a key={em} href={`mailto:${em}`} className="flex items-center gap-1.5 text-slate-700 hover:text-brand-700 break-all">
                          <Mail size={14} className="text-slate-500 flex-shrink-0" /> {em}
                        </a>
                      )) : pending}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-brand-800">
                      {loading ? '—' : fmt(counts[dept.code])}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-brand-800 text-white">
                <td className="px-4 py-3" colSpan={5}><span className="font-semibold">{tr('Total', 'மொத்தம்')}</span></td>
                <td className="px-4 py-3 text-right font-semibold">{loading ? '—' : fmt(total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
